import React, { FC, useMemo } from 'react'
import {
  ArcElement,
  Chart as ChartJS,
  DoughnutController,
  Legend,
  Tooltip,
} from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import {
  DonutChartBox,
  DonutChartBoxLg,
  DonutContainer,
  EmptyState,
  LegendDot,
  LegendItem,
  LegendLabel,
  LegendList,
  LegendValue,
} from './styles'

ChartJS.register(ArcElement, Tooltip, Legend, DoughnutController)

/** Paleta alinhada às cores já usadas nos gráficos da dashboard. */
const PALETTE = [
  '#121926',
  '#32D583',
  '#36BFFA',
  '#F79009',
  '#9E77ED',
  '#E60D1E',
  '#12B76A',
  '#CCCCCC',
]

export interface DonutItem {
  label: string
  /** valor usado para o tamanho da fatia e o cálculo do percentual */
  value: number
  /** quantidade absoluta opcional, exibida na legenda */
  quantity?: number
  color?: string
}

interface DonutIndicatorProps {
  items: DonutItem[]
  /** exibe a quantidade absoluta ao lado do percentual na legenda */
  showQuantity?: boolean
  /** estilo "foto": % dentro das fatias e rótulos ao redor com linhas, sem legenda */
  labeled?: boolean
}

const isLightColor = (hex: string): boolean => {
  const c = (hex || '').replace('#', '')
  if (c.length < 6) return false
  const r = parseInt(c.substr(0, 2), 16)
  const g = parseInt(c.substr(2, 2), 16)
  const b = parseInt(c.substr(4, 2), 16)
  return 0.299 * r + 0.587 * g + 0.114 * b > 150
}

const DonutIndicator: FC<DonutIndicatorProps> = ({ items, showQuantity, labeled }) => {
  const total = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.value) || 0), 0),
    [items]
  )

  const colors = items.map((it, i) => it.color ?? PALETTE[i % PALETTE.length])

  if (!items.length || total <= 0) {
    return <EmptyState>Sem dados no período selecionado.</EmptyState>
  }

  const data = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => Number(i.value) || 0),
        backgroundColor: colors,
        borderWidth: labeled ? 2 : 0,
        borderColor: '#fff',
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    layout: labeled ? { padding: { top: 26, bottom: 26, left: 90, right: 90 } } : undefined,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const item = items[ctx.dataIndex]
            const pct = total > 0 ? ((Number(item.value) / total) * 100).toFixed(1) : '0'
            const qty = item.quantity != null ? ` (${item.quantity})` : ''
            return ` ${item.label}: ${pct}%${qty}`
          },
        },
      },
    },
  }

  // plugin: % dentro da fatia + rótulos ao redor com anti-colisão (espaçamento mínimo)
  const arcLabelsPlugin = {
    id: 'arcLabels',
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart
      const meta = chart.getDatasetMeta(0)
      if (!meta.data.length) return

      const arcs = meta.data.map((arc: any, i: number) => {
        const p = arc.getProps(
          ['x', 'y', 'startAngle', 'endAngle', 'innerRadius', 'outerRadius'],
          true
        )
        const mid = (p.startAngle + p.endAngle) / 2
        return { i, ...p, mid, cos: Math.cos(mid), sin: Math.sin(mid) }
      })

      const cx = arcs[0].x
      const cy = arcs[0].y
      const rOut = arcs[0].outerRadius
      const rIn = arcs[0].innerRadius

      // % dentro da fatia (só nas fatias grandes o bastante)
      arcs.forEach((a: any) => {
        if (a.endAngle - a.startAngle <= 0.22) return
        const pct = total > 0 ? Math.round(((Number(items[a.i].value) || 0) / total) * 100) : 0
        const rMid = (rIn + rOut) / 2
        ctx.save()
        ctx.fillStyle = isLightColor(colors[a.i]) ? '#101828' : '#ffffff'
        ctx.font = '600 13px Inter, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(`${pct}%`, cx + a.cos * rMid, cy + a.sin * rMid)
        ctx.restore()
      })

      // rótulos externos com anti-colisão, por lado
      const MIN_GAP = 16
      const labelR = rOut + 12

      const drawSide = (arr: any[], dir: number) => {
        if (!arr.length) return
        arr.sort((p, q) => cy + p.sin * labelR - (cy + q.sin * labelR))
        const ys = arr.map((o) => cy + o.sin * labelR)
        for (let k = 1; k < ys.length; k++) {
          if (ys[k] - ys[k - 1] < MIN_GAP) ys[k] = ys[k - 1] + MIN_GAP
        }
        const xElbow = cx + dir * (rOut + 16)
        const xLabel = cx + dir * (rOut + 26)

        arr.forEach((o, k) => {
          const yL = ys[k]
          const x1 = cx + o.cos * rOut
          const y1 = cy + o.sin * rOut
          ctx.save()
          ctx.strokeStyle = '#D0D5DD'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(x1, y1)
          ctx.lineTo(xElbow, yL)
          ctx.lineTo(xLabel - dir * 4, yL)
          ctx.stroke()
          ctx.fillStyle = '#667085'
          ctx.font = '12px Inter, sans-serif'
          ctx.textAlign = dir > 0 ? 'left' : 'right'
          ctx.textBaseline = 'middle'
          ctx.fillText(items[o.i].label, xLabel, yL)
          ctx.restore()
        })
      }

      drawSide(
        arcs.filter((o: any) => o.cos >= 0),
        1
      )
      drawSide(
        arcs.filter((o: any) => o.cos < 0),
        -1
      )
    },
  }

  if (labeled) {
    return (
      <DonutChartBoxLg>
        <Doughnut data={data} options={options as any} plugins={[arcLabelsPlugin]} />
      </DonutChartBoxLg>
    )
  }

  return (
    <DonutContainer>
      <DonutChartBox>
        <Doughnut data={data} options={options as any} />
      </DonutChartBox>
      <LegendList>
        {items.map((item, i) => {
          const pct = total > 0 ? ((Number(item.value) / total) * 100).toFixed(1) : '0'
          return (
            <LegendItem key={`${item.label}-${i}`}>
              <LegendDot style={{ background: colors[i] }} />
              <LegendLabel title={item.label}>{item.label}</LegendLabel>
              <LegendValue>
                {showQuantity && item.quantity != null ? `${item.quantity} · ` : ''}
                {pct}%
              </LegendValue>
            </LegendItem>
          )
        })}
      </LegendList>
    </DonutContainer>
  )
}

export default DonutIndicator
